'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, X, Package, Tags, Truck, Users, MapPin, Bot } from 'lucide-react';

import { useTranslation } from '@/hooks/useTranslation';
import { colors, typography } from '@/styles/theme';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { searchApi, type GlobalSearchResult, type SearchHit } from './api';
import { useDebounce } from '@/hooks/useDebounce';

const TYPE_ICON: Record<SearchHit['type'], typeof Package> = {
  unit: Bot,
  model: Package,
  sku: Tags,
  supplier: Truck,
  customer: Users,
  location: MapPin,
};

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const rm = t.robotManager;
  const inputRef = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState('');
  const debounced = useDebounce(q, 250);
  const [result, setResult] = useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setQ('');
      setResult(null);
      // 聚焦
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!debounced.trim()) {
      setResult(null);
      return;
    }
    setLoading(true);
    searchApi
      .search(debounced)
      .then(setResult)
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [debounced]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const goTo = (hit: SearchHit) => {
    router.push(hit.href);
    onClose();
  };

  if (!open) return null;

  const sections: Array<{ key: keyof GlobalSearchResult; label: string }> = [
    { key: 'units', label: rm.search.units },
    { key: 'models', label: rm.search.models },
    { key: 'skus', label: rm.search.skus },
    { key: 'suppliers', label: rm.search.suppliers },
    { key: 'customers', label: rm.search.customers },
    { key: 'locations', label: rm.search.locations },
  ];
  const totalHits = result
    ? sections.reduce((acc, s) => acc + (result[s.key]?.length ?? 0), 0)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg shadow-xl flex flex-col max-h-[75vh]"
        style={{ backgroundColor: colors.bgPrimary }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 搜索框 */}
        <div
          className="flex items-center gap-2 px-4 py-3"
          style={{ borderBottom: `1px solid ${colors.border}` }}
        >
          <Search className="w-4 h-4 shrink-0" style={{ color: colors.textTertiary }} />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={rm.search.placeholder}
            className="flex-1 border-none shadow-none focus-visible:ring-0 h-8 px-0"
          />
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* 结果区 */}
        <div className="flex-1 overflow-y-auto">
          {!q.trim() ? (
            <div className="py-12 text-center text-sm" style={{ color: colors.textTertiary }}>
              {rm.search.hintEmpty}
            </div>
          ) : loading ? (
            <div className="py-12 text-center text-sm" style={{ color: colors.textTertiary }}>
              {rm.loading}
            </div>
          ) : totalHits === 0 ? (
            <div className="py-12 text-center text-sm" style={{ color: colors.textTertiary }}>
              {rm.search.noResults}
            </div>
          ) : (
            sections.map((sec) => {
              const hits = result?.[sec.key] ?? [];
              if (hits.length === 0) return null;
              return (
                <div key={sec.key}>
                  <div
                    className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: colors.textTertiary, backgroundColor: colors.bgSecondary }}
                  >
                    {sec.label} · {hits.length}
                  </div>
                  {hits.map((hit) => {
                    const Icon = TYPE_ICON[hit.type];
                    return (
                      <button
                        key={hit.id}
                        onClick={() => goTo(hit)}
                        className="w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors hover:bg-gray-50"
                        style={{ borderBottom: `1px solid ${colors.border}` }}
                      >
                        <div
                          className="w-7 h-7 rounded flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`${typography.body} font-medium truncate`}>{hit.title}</div>
                          {hit.subtitle && (
                            <div className={`${typography.caption} truncate`}>{hit.subtitle}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* 脚注：键盘提示 */}
        <div
          className="px-4 py-2 text-xs flex items-center gap-3"
          style={{ color: colors.textTertiary, borderTop: `1px solid ${colors.border}` }}
        >
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-mono">Esc</kbd> {rm.search.hintClose}
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-mono">↵</kbd> {rm.search.hintEnter}
          </span>
        </div>
      </div>
    </div>
  );
}
